import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Skeleton,
  alpha,
  LinearProgress,
  SelectChangeEvent,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Download as ExportIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  LocalHospital as DoctorIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CheckCircle as CompletedIcon,
  Pending as PendingIcon,
  Cancel as CancelledIcon,
  PlayArrow as InProgressIcon,
  Schedule as ScheduledIcon,
  Today as TodayIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import { RootState } from '../../store';
import axios from 'axios';

// Types
interface AppointmentListItem {
  id: number;
  bookingNumber: string;
  appointmentDate: string;
  appointmentTime: string;
  endTime: string;
  appointmentType: string;
  appointmentTypeColor: string;
  priority: string;
  priorityColor: string;
  status: string;
  statusColor: string;
  officerId: number;
  officerName: string;
  officerCadre: string;
  officerMobile: string;
  doctorId: number;
  doctorName: string;
  doctorSpecialization: string;
  doctorMobile: string;
  visitAddress: string;
  visitCity: string;
  reasonForVisit: string;
  symptoms: string;
  paymentStatus: string;
  paymentStatusColor: string;
  consultationFee: number;
  visitFee: number;
  totalAmount: number;
  createdAt: string;
  updatedAt: string;
}

interface AppointmentStatistics {
  totalToday: number;
  pendingToday: number;
  acceptedToday: number;
  inProgressToday: number;
  completedToday: number;
  cancelledToday: number;
  totalThisWeek: number;
  completedThisWeek: number;
  cancelledThisWeek: number;
  totalThisMonth: number;
  completedThisMonth: number;
  completionRate: number;
  cancellationRate: number;
  statusDistribution: { status: string; displayName: string; count: number; color: string }[];
  typeDistribution: { type: string; displayName: string; count: number; color: string }[];
  priorityDistribution: { priority: string; displayName: string; count: number; color: string }[];
  timeSlotDistribution: { timeSlot: string; count: number }[];
}

const AppointmentControlPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState<AppointmentListItem[]>([]);
  const [statistics, setStatistics] = useState<AppointmentStatistics | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentListItem | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const fetchAppointments = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams({
        page: page.toString(),
        size: rowsPerPage.toString(),
        search: searchQuery,
        status: statusFilter,
        appointmentType: typeFilter,
        priority: priorityFilter,
      });

      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/appointments?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAppointments(response.data.data.content);
        setTotalElements(response.data.data.totalElements);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, statusFilter, typeFilter, priorityFilter]);

  const fetchStatistics = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/v1/admin/appointments/statistics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, []);

  useEffect(() => {
    fetchAppointments();
    fetchStatistics();
  }, [fetchAppointments, fetchStatistics]);

  const handleActionOpen = (event: React.MouseEvent<HTMLElement>, appointment: AppointmentListItem) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedAppointment(appointment);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedAppointment(null);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING': return <PendingIcon />;
      case 'ACCEPTED': return <ScheduledIcon />;
      case 'IN_PROGRESS': return <InProgressIcon />;
      case 'VISITED': return <DoctorIcon />;
      case 'CLOSED': return <CompletedIcon />;
      case 'CANCELLED': return <CancelledIcon />;
      default: return <PendingIcon />;
    }
  };

  const statCards = statistics ? [
    { title: "Today's Total", value: statistics.totalToday, icon: <CalendarIcon />, color: '#2563eb' },
    { title: 'Pending', value: statistics.pendingToday, icon: <PendingIcon />, color: '#f59e0b' },
    { title: 'In Progress', value: statistics.inProgressToday, icon: <InProgressIcon />, color: '#8b5cf6' },
    { title: 'Completed', value: statistics.completedToday, icon: <CompletedIcon />, color: '#10b981' },
    { title: 'Cancelled', value: statistics.cancelledToday, icon: <CancelledIcon />, color: '#ef4444' },
    { title: 'This Month', value: statistics.totalThisMonth, icon: <TrendingIcon />, color: '#0ea5e9' },
  ] : [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Appointment Control Center
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Monitor and manage all healthcare appointments
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchAppointments(); fetchStatistics(); }}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color, mt: 0.5 }}>
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(stat.color, 0.1),
                    color: stat.color,
                  }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Status Distribution */}
      {statistics && statistics.statusDistribution.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Status Distribution
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {statistics.statusDistribution.map((item, index) => (
                <Chip
                  key={index}
                  label={`${item.displayName}: ${item.count}`}
                  size="small"
                  sx={{
                    borderRadius: 1,
                    fontWeight: 500,
                    bgcolor: alpha(item.color, 0.1),
                    color: item.color,
                    border: `1px solid ${alpha(item.color, 0.3)}`,
                  }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: '1px solid #e2e8f0' }}>
          <Tab label={`All (${totalElements})`} sx={{ textTransform: 'none' }} />
          <Tab label="Pending" sx={{ textTransform: 'none' }} />
          <Tab label="In Progress" sx={{ textTransform: 'none' }} />
          <Tab label="Completed" sx={{ textTransform: 'none' }} />
          <Tab label="Cancelled" sx={{ textTransform: 'none' }} />
        </Tabs>

        {/* Search & Filter */}
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
          <TextField
            size="small"
            placeholder="Search appointments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="ACCEPTED">Accepted</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="CLOSED">Completed</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="HOME_VISIT">Home Visit</MenuItem>
              <MenuItem value="VIDEO_CONSULTATION">Video Consultation</MenuItem>
              <MenuItem value="EMERGENCY_VISIT">Emergency</MenuItem>
              <MenuItem value="FOLLOW_UP">Follow-up</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priorityFilter}
              label="Priority"
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="LOW">Low</MenuItem>
              <MenuItem value="NORMAL">Normal</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="URGENT">Urgent</MenuItem>
              <MenuItem value="EMERGENCY">Emergency</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'transparent' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Booking #</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Date & Time</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Officer</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Doctor</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Payment</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : appointments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography sx={{ color: '#64748b' }}>No appointments found</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                appointments.map((appointment) => (
                  <TableRow key={appointment.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {appointment.bookingNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {appointment.appointmentDate}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {appointment.appointmentTime}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'transparent' , fontSize: '0.75rem' }}>
                          {appointment.officerName?.charAt(0) || 'O'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {appointment.officerName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {appointment.officerCadre}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'transparent' , fontSize: '0.75rem' }}>
                          {appointment.doctorName?.charAt(0) || 'D'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {appointment.doctorName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {appointment.doctorSpecialization}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={appointment.appointmentType?.replace('_', ' ') || 'N/A'}
                        size="small"
                        sx={{
                          bgcolor: alpha(appointment.appointmentTypeColor || '#6b7280', 0.1),
                          color: appointment.appointmentTypeColor || '#6b7280',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={appointment.priority}
                        size="small"
                        sx={{
                          bgcolor: alpha(appointment.priorityColor || '#6b7280', 0.1),
                          color: appointment.priorityColor || '#6b7280',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(appointment.status)}
                        label={appointment.status}
                        size="small"
                        sx={{
                          bgcolor: alpha(appointment.statusColor || '#6b7280', 0.1),
                          color: appointment.statusColor || '#6b7280',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={appointment.paymentStatus}
                        size="small"
                        sx={{
                          bgcolor: alpha(appointment.paymentStatusColor || '#6b7280', 0.1),
                          color: appointment.paymentStatusColor || '#6b7280',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleActionOpen(e, appointment)}>
                        <MoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={handleActionClose}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleActionClose}>
          <ListItemIcon><DoctorIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Doctor</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleActionClose}>
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Officer</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleActionClose}>
          <ListItemIcon><LocationIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Location</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AppointmentControlPage;